import {
  Stack,
  Box,
  styled,
  Popover,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useIntl } from "react-intl";
import {
  VERIFIED2_ICON,
  UNVERIFIED_ICON,
  RoundedVerifyIcon,
  UnverifiedFieldIcon,
} from "Assets/icons";
import { Text } from "Components/Text";
import { useMemo, useState } from "react";
import Tooltip from "./Tooltip";
import { Img } from "Components/Img";
import { COLORS } from "constants/layout";

// Verification types
export enum VerificationType {
  WORK_EMAIL = "work_email",
  BUSINESS = "business",
  PHONE = "phone",
  IDENTITY = "identity",
  // Add more types as needed
}

// Verification level
export enum VerificationLevel {
  NONE = 0,
  BASIC = 1,
  STANDARD = 2,
  PREMIUM = 3,
  ENTERPRISE = 4,
}

// Base interfaces
export interface VerificationItem {
  label: string;
  show: boolean | number;
}

export interface VerificationStatusItem {
  type: VerificationType;
  isVerified: boolean;
  level?: VerificationLevel;
}

export interface VerificationConfig {
  title?: string;
  entity?: string;
  iconHeight?: number;
  customTooltipItems?: string[];
  verificationItems?: VerificationItem[];
  verificationStatuses: VerificationStatusItem[];
  customIcon?: {
    verified?: string;
    unverified?: string;
  };
  customTitle?: {
    verified?: string;
    unverified?: string;
  };
  // Custom verification rules
  verificationRules?: {
    minLevel?: VerificationLevel;
    requiredTypes?: VerificationType[];
  };
}

// Default configuration
const DEFAULT_CONFIG: Partial<VerificationConfig> = {
  entity: "Company",
  iconHeight: 12,
  customIcon: {
    verified: VERIFIED2_ICON,
    unverified: UNVERIFIED_ICON,
  },
  customTitle: {
    verified: "entity_is_verified_by_freshdi",
    unverified: "entity_is_not_verified",
  },
  verificationRules: {
    minLevel: VerificationLevel.BASIC,
    requiredTypes: [VerificationType.WORK_EMAIL, VerificationType.BUSINESS],
  },
};

// Component props extending the config
interface VerificationStatusProps
  extends Omit<VerificationConfig, "verificationStatuses"> {
  // New simple props
  is_featured?: number;
  is_verified?: number;
  data?: {
    // Verification fields
    is_verified_work_email?: number;
    is_biz_verified?: number; // Changed to number to match other verification fields
    is_verified_phone?: number;
    is_verified_identity?: number;
    verification_level?: number;
  };
}

const VerificationStatus = ({
  title,
  customTooltipItems = [],
  iconHeight,
  entity,
  customIcon,
  customTitle,
  verificationItems = [],
  verificationRules,
  // New simple props
  is_featured,
  is_verified,
  data,
}: VerificationStatusProps) => {
  const t = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Convert verification props to VerificationStatus array
  const verificationStatuses = useMemo(() => {
    const statuses: VerificationStatusItem[] = [];

    // Handle simple props first
    if (typeof is_verified === "number") {
      statuses.push({
        type: VerificationType.BUSINESS,
        isVerified: Boolean(is_verified),
        level: data?.verification_level as VerificationLevel,
      });
    }

    if (data?.is_biz_verified) {
      statuses.push({
        type: VerificationType.BUSINESS,
        isVerified: true,
        level: data?.verification_level as VerificationLevel,
      });
    }

    // Handle existing props from data
    if (typeof data?.is_verified_work_email === "number") {
      statuses.push({
        type: VerificationType.WORK_EMAIL,
        isVerified: Boolean(data.is_verified_work_email),
        level: data?.verification_level as VerificationLevel,
      });
    }

    if (typeof data?.is_biz_verified === "number") {
      statuses.push({
        type: VerificationType.BUSINESS,
        isVerified: Boolean(data.is_biz_verified),
        level: data?.verification_level as VerificationLevel,
      });
    }

    if (typeof data?.is_verified_phone === "number") {
      statuses.push({
        type: VerificationType.PHONE,
        isVerified: Boolean(data.is_verified_phone),
        level: data?.verification_level as VerificationLevel,
      });
    }

    if (typeof data?.is_verified_identity === "number") {
      statuses.push({
        type: VerificationType.IDENTITY,
        isVerified: Boolean(data.is_verified_identity),
        level: data?.verification_level as VerificationLevel,
      });
    }

    return statuses;
  }, [
    data?.is_verified_work_email,
    data?.is_biz_verified,
    data?.is_verified_phone,
    data?.is_verified_identity,
    data?.verification_level,
    is_verified,
  ]);

  // Merge default config with props
  const config = useMemo(
    () => ({
      ...DEFAULT_CONFIG,
      iconHeight,
      entity,
      customIcon: {
        ...DEFAULT_CONFIG.customIcon,
        ...customIcon,
      },
      customTitle: {
        ...DEFAULT_CONFIG.customTitle,
        ...customTitle,
      },
      verificationRules: {
        ...DEFAULT_CONFIG.verificationRules,
        ...verificationRules,
      },
    }),
    [iconHeight, entity, customIcon, customTitle, verificationRules]
  );

  // Determine overall verification status
  const isVerified = useMemo(() => {
    // Check if any of the four verification types is verified
    return Boolean(
      data?.is_biz_verified ||
        data?.is_verified_work_email ||
        data?.is_verified_phone ||
        data?.is_verified_identity
    );
  }, [
    data?.is_biz_verified,
    data?.is_verified_work_email,
    data?.is_verified_phone,
    data?.is_verified_identity,
  ]);

  // Get the title based on verification status
  // const tooltipTitle = useMemo(() => {
  //   if (title) return t.formatMessage({ id: title }, { entity: config.entity });
  //   const hasAnyVerification = verificationStatuses.some(
  //     (status) => status.isVerified
  //   );
  //   return hasAnyVerification
  //     ? t.formatMessage(
  //         { id: "entity_is_verified_by_freshdi" },
  //         { entity: config.entity }
  //       )
  //     : t.formatMessage(
  //         { id: config.customTitle?.unverified },
  //         { entity: config.entity }
  //       );
  // }, [title, verificationStatuses, config.customTitle, config.entity, t]);

  // Get the appropriate icon
  const icon = useMemo(() => {
    return isVerified
      ? config.customIcon?.verified
      : config.customIcon?.unverified;
  }, [isVerified, config.customIcon]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const tooltipContent = (
    <StyledTooltip>
      <Text
        mb={1.5}
        color={COLORS.green[600]}
        sx={{
          fontSize: "14px!important",
          textAlign: "center",
          fontWeight: 500,
        }}
      >
        {t.formatMessage({ id: "verification_details" })}
      </Text>
      <ul style={{ paddingLeft: 0 }}>
        {[
          {
            isVerified: data?.is_biz_verified,
            text: t.formatMessage({ id: "verified_business" }),
          },
          {
            isVerified: data?.is_verified_work_email,
            text: t.formatMessage({ id: "verified_work_email" }),
          },
          {
            isVerified: data?.is_verified_phone,
            text: t.formatMessage({ id: "verified_phone" }),
          },
          {
            isVerified: data?.is_verified_identity,
            text: t.formatMessage({ id: "verified_identity" }),
          },
        ]
          .sort((a, b) => (b.isVerified ? 1 : 0) - (a.isVerified ? 1 : 0))
          .map((item, index) => (
            <li key={index}>
              <Stack direction="row" gap={1} alignItems="center">
                {item.isVerified ? (
                  <RoundedVerifyIcon />
                ) : (
                  <UnverifiedFieldIcon />
                )}
                <Text
                  sx={{
                    color: item.isVerified ? "inherit" : "text.secondary",
                  }}
                >
                  {item.text}
                </Text>
              </Stack>
            </li>
          ))}
        {/* Temporarily comment out verification level
        {data?.verification_level && data.verification_level > 0 && (
          <li>
            <Stack direction="row" gap={1} alignItems="center">
              <RoundedVerifyIcon />
              <Text>
                {t.formatMessage({
                  id: `verification_level_${data.verification_level}`,
                })}
              </Text>
            </Stack>
          </li>
        )}
        */}
      </ul>
      {customTooltipItems.length > 0 && (
        <ul>
          {customTooltipItems.map((item, i) => (
            <li key={i}>
              <Text>{t.formatMessage({ id: item })}</Text>
            </li>
          ))}
        </ul>
      )}
    </StyledTooltip>
  );

  return (
    <Stack direction="row" spacing={1}>
      {isMobile ? (
        <>
          <div onClick={handleClick}>
            <Img
              src={icon}
              alt={
                verificationStatuses.some((status) => status.isVerified)
                  ? "verified"
                  : "unverified"
              }
              height={config.iconHeight}
              style={{
                width: "auto",
                height: verificationStatuses.some((status) => status.isVerified)
                  ? "12px"
                  : `${config.iconHeight}px`,
                objectFit: "contain",
              }}
            />
          </div>
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
            PaperProps={{
              sx: {
                maxWidth: "280px",
                p: 1,
                boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
                borderRadius: "8px",
              },
            }}
          >
            {tooltipContent}
          </Popover>
        </>
      ) : (
        <Tooltip title={tooltipContent}>
          <div>
            <Img
              src={icon}
              alt={
                verificationStatuses.some((status) => status.isVerified)
                  ? "verified"
                  : "unverified"
              }
              height={config.iconHeight}
              style={{
                width: "auto",
                height: verificationStatuses.some((status) => status.isVerified)
                  ? "12px"
                  : `${config.iconHeight}px`,
                objectFit: "contain",
              }}
            />
          </div>
        </Tooltip>
      )}
    </Stack>
  );
};

const StyledTooltip = styled(Box)({
  padding: 10,
  backgroundColor: COLORS.gray[50],
  borderRadius: 4,
  p: {
    fontSize: 12,
    "&::first-letter": {
      textTransform: "uppercase",
    },
  },
  ul: {
    paddingInlineStart: 10,
    margin: 0,
    listStyle: "none",
  },
  "& ul li": {
    marginBottom: 8,
  },
  "& ul li p": {
    margin: 0,
    "&::first-letter": {
      textTransform: "uppercase",
    },
  },
});

export default VerificationStatus;
